<template>
  <div
    class="tpl-card tpl-item-card"
    :class="{ 'is-in-folder': inFolder && canManage, 'is-manageable': canManage, 'is-selected': isSelected }"
    :data-tpl-id="tpl.id"
    :data-thumb-fallback="tpl.thumbFallback || ''"
    :draggable="canManage"
    @click="$emit('click', tpl)"
    @dragstart="canManage && $emit('dragstart', tpl, $event)"
    @dragend="$emit('dragend', $event)"
  >
    <div class="tpl-thumb">
      <button
        v-if="canManage"
        type="button"
        class="select-control"
        :class="{ active: isSelected }"
        :aria-label="isSelected ? `取消选择${tpl.name}` : `选择${tpl.name}`"
        @click.stop="$emit('toggle-select', tpl.id, $event)"
      >{{ isSelected ? '✓' : '' }}</button>

      <img
        :src="thumb"
        :alt="tpl.name"
        loading="lazy"
        draggable="false"
        @error="$emit('thumb-error', $event, tpl)"
      />
      <span class="tpl-badge" :class="[tpl.type, { 'has-select': canManage, pending: tpl.pendingMasterEdit }]">
        {{ tpl.pendingMasterEdit ? '待保存' : (tpl.type === 'master' ? '平台模版' : '店铺模版') }}
      </span>
      <span class="zoom-hint">点击预览</span>

      <div v-if="canManage" class="card-actions">
        <button
          v-if="inFolder"
          type="button"
          class="btn-action"
          @click.stop="$emit('move-out', tpl)"
        >移出</button>
        <button
          v-if="canRename"
          type="button"
          class="btn-action"
          @click.stop="$emit('rename', tpl)"
        >重命名</button>
        <button
          v-if="canDelete"
          type="button"
          class="btn-action danger"
          @click.stop="$emit('delete', tpl)"
        >删除</button>
      </div>
    </div>

    <div class="tpl-body">
      <div class="tpl-info-col">
        <h3 :title="tpl.name">{{ tpl.name }}</h3>
        <div class="tpl-meta">
          <span class="tpl-tag">{{ tpl.category }}</span>
          <span>{{ tpl.size }}</span>
        </div>
      </div>
      <button type="button" class="btn-use-pill" title="立即使用此模版进行设计" @click.stop="$emit('click', tpl)">
        立即使用
      </button>
    </div>
  </div>
</template>

<script setup>
defineProps({
  tpl: {
    type: Object,
    required: true,
  },
  thumb: {
    type: String,
    default: '',
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  canRename: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  inFolder: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['click', 'toggle-select', 'move-out', 'rename', 'delete', 'dragstart', 'dragend', 'thumb-error']);
</script>

<style scoped>
.tpl-card {
  border-radius: 14px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s ease;
  position: relative;
  cursor: pointer;
}

.tpl-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-3px);
  box-shadow: 0 14px 28px -4px rgba(15, 23, 42, 0.1);
}

.tpl-item-card.is-manageable { cursor: grab; }
.tpl-item-card.is-manageable:active { cursor: grabbing; }

.tpl-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  min-height: 140px;
  background: #f8fafc;
  background-image: radial-gradient(#e2e8f0 1.2px, transparent 1.2px);
  background-size: 10px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #f1f5f9;
}

.tpl-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
  pointer-events: none;
  transition: transform 0.25s ease;
}

.tpl-card:hover .tpl-thumb img {
  transform: scale(1.04);
}

.tpl-card.is-selected {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.2);
}

.select-control {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 5;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.15s ease;
  cursor: pointer;
}

.select-control:hover {
  border-color: var(--color-primary);
}

.select-control.active {
  border-color: var(--color-primary);
  background: var(--color-primary);
}

.tpl-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.tpl-badge.has-select {
  left: 36px;
}

.tpl-badge.master {
  background: rgba(219, 234, 254, 0.95);
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.tpl-badge.store {
  background: rgba(241, 245, 249, 0.95);
  color: var(--color-badge-store-text);
  border: 1px solid var(--color-badge-store-border);
}

.tpl-badge.pending {
  background: rgba(254, 243, 199, 0.95);
  color: #b45309;
  border: 1px solid #fde68a;
}

.zoom-hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: #ffffff;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.tpl-card:hover .zoom-hint {
  opacity: 1;
}

.card-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  display: flex;
  gap: 4px;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  pointer-events: none;
}

.tpl-card:hover .card-actions {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.btn-action {
  padding: 3px 10px;
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid var(--border-color, #cbd5e1);
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
  cursor: pointer;
}

.btn-action.danger {
  border-color: #ffa39e;
  background: #fff1f0;
  color: #cf1322;
}

.btn-action:hover {
  filter: brightness(0.97);
}

.tpl-body {
  padding: 10px 12px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #ffffff;
}

.tpl-info-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tpl-body h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tpl-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}

.tpl-tag {
  color: #7c3aed;
  background: #f5f3ff;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 10px;
}

.btn-use-pill {
  padding: 4px 12px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.btn-use-pill:hover {
  background: #7c3aed;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}
</style>
